import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  Dropdown,
  DropdownMenu,
  DropdownToggle,
  Row,
} from "reactstrap";
import InterviewDatePicker from "./InterviewDatePicker";
import { format, parseISO } from "date-fns";
import "./AnalyticsCard.scss";
import { fetchInterviewCounts } from "../../store/actions";
import { datePickerConstants } from "../../pages/interviewListingConstants";
import { statusMapping } from "./AnalyticsCardConstants";

const AnalyticsCard = ({ onDateRangeChange, handleStatus, status }) => {
  const dispatch = useDispatch();
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [selectedDates, setSelectedDates] = useState({});
  const [selectedRangeLabel, setSelectedRangeLabel] = useState("This Week");
  const [selectedStatus, setSelectedStatus] = useState(null);

  const thisWeek = datePickerConstants.find(
    (dateRange) => Object.keys(dateRange)[0] === "This Week"
  )["This Week"];

  //Store from and to dates state
  const [fromDate, setFromDate] = useState(
    thisWeek.from ? format(thisWeek.from, "yyyy-MM-dd") : null
  );
  const [toDate, setToDate] = useState(
    thisWeek.to ? format(thisWeek.to, "yyyy-MM-dd") : null
  );

  //Fetch data
  const interviewCountData = useSelector(
    (state) => state?.interviewAnalyticsReducer?.interviewCounts?.data
  );

  //Payload
  const requestPayload = {
    startDate: fromDate,
    endDate: toDate,
  };

  //Fetch data when component loads
  useEffect(() => {
    dispatch(fetchInterviewCounts(requestPayload));
  }, [fromDate, toDate, dispatch]);

  // Data for the progress bars
  const analyticsData = [
    {
      label: "Scheduled",
      value: interviewCountData?.scheduledCount,
      progress:
        interviewCountData?.scheduledCount > 0
          ? (interviewCountData?.scheduledCount /
              interviewCountData?.scheduledCount) *
            100
          : 1,
    },
    {
      label: "Re-scheduled",
      value: interviewCountData?.rescheduledCount,
      progress:
        interviewCountData?.rescheduledCount > 0
          ? (interviewCountData?.rescheduledCount /
              interviewCountData?.scheduledCount) *
            100
          : 1,
    },
    {
      label: "Completed",
      value: interviewCountData?.completedCount,
      progress:
        interviewCountData?.completedCount > 0
          ? (interviewCountData?.completedCount /
              interviewCountData?.scheduledCount) *
            100
          : 1,
    },
    {
      label: "Cancelled",
      value: interviewCountData?.cancelledCount,
      progress:
        interviewCountData?.cancelledCount > 0
          ? (interviewCountData?.cancelledCount /
              interviewCountData?.scheduledCount) *
            100
          : 1,
    },
  ];

  const formatDates = (date) => {
    if (!date) return null;
    return format(parseISO(date), "yyyy-MM-dd");
  };

  const handleDateRangeChange = (from, to, label) => {
    const formattedFrom = formatDates(from);
    const formattedTo = formatDates(to);
    setSelectedRangeLabel(label);
    setFromDate(formattedFrom);
    setToDate(formattedTo);
    onDateRangeChange(formattedFrom, formattedTo);
  };

  return (
    <Col md="6" className="d-flex">
      <Card className="shadow-sm rounded flex-grow-1">
        <CardBody className="p-4">
          <div className="d-flex flex-row mb-2">
            <div className="flex-grow-1">
              <CardTitle style={{ fontWeight: "bold" }}>Analytics</CardTitle>
            </div>
            <div className="flex-grow-2">
              <Dropdown
                isOpen={openDatePicker}
                toggle={() => setOpenDatePicker(!openDatePicker)}
              >
                <DropdownToggle color="light" className="border">
                  <div>
                    <span className="mdi mdi-calendar-month-outline me-2"></span>
                    <span className="me-3 custom-dr-placeholder">
                      <strong>{selectedRangeLabel}</strong>
                    </span>
                  </div>
                </DropdownToggle>
                <DropdownMenu className="p-3 analyticscard-dropdown-menu">
                  <InterviewDatePicker
                    setOpenDatePicker={setOpenDatePicker}
                    selectedDatesMain={selectedDates}
                    setSelectedDatesMain={setSelectedDates}
                    onDateRange={handleDateRangeChange}
                  />
                </DropdownMenu>
              </Dropdown>
            </div>
          </div>
          <div
            className="p-2 rounded"
            style={{
              background: "linear-gradient(to right, #EDF1FA, #EBF3FF)",
            }}
          >
            {analyticsData.map((item, index) => (
              <div
                key={index}
                className="d-flex flex-row align-items-center cursor-pointer"
                onClick={() => handleStatus(item?.label)}
                style={{
                  padding: "12px",
                  borderRadius:
                    index === 0
                      ? "10px 10px 0 0"
                      : index === analyticsData.length - 1
                      ? "0 0 8px 8px"
                      : "0", // rounded corners only on top and bottom rows
                }}
              >
                <div className="d-flex flex-row me-2" style={{ width: "25%" }}>
                  <div
                    className={`${
                      status === statusMapping[item.label]
                        ? "status-selected"
                        : ""
                    } status-interview`}
                  >
                    <div
                      className={`${
                        status === statusMapping[item.label]
                          ? "value-selected"
                          : "status-value"
                      } "d-flex ps-2"`}
                    >
                      {item.value}
                    </div>
                    <div className="ps-3">
                      <span>{item.label}</span>
                    </div>
                  </div>
                </div>
                <div className="d-flex flex-grow-1 ms-0 ps-0">
                  <div
                    style={{
                      width: `${item.progress}%`,
                      height: "22px",
                      background: "linear-gradient(to right, #A5BEE3, #4F86F7)",
                      transition: "width 0.3s ease",
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </CardBody>
      </Card>
    </Col>
  );
};

export default AnalyticsCard;
